import axios, { type AxiosError } from 'axios';
import { z } from 'zod';
import { Endpoints } from 'utils/endpoints';
import type { EmailTemplateRepository } from '../interfaces/EmailTemplateRepository';
import type {
  EmailTemplate,
  DeleteEmailTemplateRequest,
  UpsertEmailTemplateRequest,
  EmailTemplateScope,
  EmailTemplatesQueryParamsRequest,
} from 'pages/settings/email-templates/types';
import { EMAIL_TEMPLATE_SCOPE, EMAIL_TEMPLATE_VERSION_SEGMENT } from 'pages/settings/email-templates/types';
import { apiClient, notificationPreferencesClient } from 'services/api/client';
import { trackError } from '@proposify/frontend-observability';
import { trackValidationFailure } from 'services/notifications/validationTracking';

const deleteEmailTemplateResponseSchema = z.object({
  success: z.literal(true),
  message: z.string().optional(),
});

const emailTemplateApiItemSchema = z.object({
  id: z.string().optional(),
  createdBy: z.string().optional(),
  eventType: z.string(),
  html: z.string(),
  name: z.string(),
  subject: z.string(),
  versionSegment: z.string(),
  options: z.record(z.unknown()).optional(),
  text: z.string().optional().nullable(),
  updatedAt: z.string().optional().nullable(),
});

const emailTemplateListResponseSchema = z.object({
  items: z.array(emailTemplateApiItemSchema),
  nextToken: z.string().optional(),
});

const emailLegacyTemplateListResponseSchema = z.array(z.object({ title: z.string(), id: z.string() }));

const upsertEmailTemplatesResponseSchema = z.array(emailTemplateApiItemSchema);

type ParseResponseMetadata = {
  label: string;
  cause: string;
  userMessage: string;
  extraContext?: Record<string, unknown>;
};

function asResponseRecordForTracking(value: unknown): Record<string, unknown> {
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return { _value: value as unknown };
}

function parseAndValidateResponse<T extends z.ZodTypeAny>(schema: T, raw: unknown, meta: ParseResponseMetadata): z.infer<T> {
  const parsed = schema.safeParse(raw);

  if (!parsed.success) {
    trackValidationFailure({
      label: meta.label,
      cause: meta.cause,
      error: parsed.error,
      responseObj: asResponseRecordForTracking(raw),
      extraContext: meta.extraContext,
    });
    throw new Error(meta.userMessage);
  }

  return parsed.data;
}

function mapApiError(error: unknown, operation: string, endpoint: string): never {
  if (!axios.isAxiosError(error)) {
    throw error;
  }

  const axiosError = error as AxiosError<{ message?: string }>;
  const operationLabel = `ApiEmailTemplateRepository.${operation}`;

  trackError('[Email templates] Notification-service request failed', {
    level: 'error',
    endpoint,
    operation: operationLabel,
    message: axiosError.message,
    stack: axiosError?.stack ?? new Error().stack,
    responseStatus: axiosError.response?.status,
    errorFrom: 'frontend',
    cause: `Notification-service request failed during ${operationLabel}`,
  });

  const status = axiosError?.response?.status;

  const errorMessages: Record<number, string> = {
    400: 'Invalid request',
    403: 'Operation not allowed',
    404: 'Email template not found',
    409: 'Email template already exists',
  };

  throw new Error(errorMessages[status ?? 0] ?? 'Email template operation failed with an unspecified error');
}

function buildListParams(scope: EmailTemplateScope, overrides?: EmailTemplatesQueryParamsRequest): EmailTemplatesQueryParamsRequest {
  return { scope, ...overrides };
}

export const ApiEmailTemplateRepository: EmailTemplateRepository = {
  delete: async (request: DeleteEmailTemplateRequest): Promise<void> => {
    const url = Endpoints.deleteEmailTemplate(request.eventType, request.versionSegment);

    try {
      const response = await notificationPreferencesClient.delete<{ message?: string; success?: boolean } | undefined>(url);
      parseAndValidateResponse(deleteEmailTemplateResponseSchema, response, {
        label: 'Delete email template',
        cause: 'ApiEmailTemplateRepository.delete response schema validation failed',
        userMessage: 'Failed to delete email template',
        extraContext: { request, response },
      });
    } catch (error) {
      mapApiError(error, 'delete', url);
    }
  },
  getAll: async (params?: EmailTemplatesQueryParamsRequest): Promise<EmailTemplate[]> => {
    const proposifyParams = buildListParams(EMAIL_TEMPLATE_SCOPE.PROPOSIFY, params);
    const accountParams = buildListParams(EMAIL_TEMPLATE_SCOPE.ACCOUNT, params);
    const proposifyUrl = Endpoints.listEmailTemplates(proposifyParams);
    const accountUrl = Endpoints.listEmailTemplates(accountParams);

    try {
      const [responseProposify, responseAccount] = await Promise.all([
        notificationPreferencesClient.get(proposifyUrl),
        notificationPreferencesClient.get(accountUrl),
      ]);

      const listProposify = parseAndValidateResponse(emailTemplateListResponseSchema, responseProposify, {
        label: 'Email templates list (proposify)',
        cause: 'ApiEmailTemplateRepository.getAll proposify list response schema validation failed',
        userMessage: 'Could not load proposify email templates',
        extraContext: { scope: EMAIL_TEMPLATE_SCOPE.PROPOSIFY, params },
      });
      const listAccount = parseAndValidateResponse(emailTemplateListResponseSchema, responseAccount, {
        label: 'Email templates list (account)',
        cause: 'ApiEmailTemplateRepository.getAll account list response schema validation failed',
        userMessage: 'Could not load account email templates',
        extraContext: { scope: EMAIL_TEMPLATE_SCOPE.ACCOUNT, params },
      });

      const proposify = listProposify.items.map((item) => ({ ...item, scope: EMAIL_TEMPLATE_SCOPE.PROPOSIFY }));
      const account = listAccount.items.map((item) => ({ ...item, scope: EMAIL_TEMPLATE_SCOPE.ACCOUNT }));
      return [...proposify, ...account] as EmailTemplate[];
    } catch (error) {
      mapApiError(error, 'getAll', proposifyUrl);
    }
  },
  // Legacy method - kept for now, to be removed in the future
  getAllLegacyEmailTemplatesLegacy: async () => {
    try {
      const response = await apiClient.get(Endpoints.getAllLegacyEmailTemplates);

      return parseAndValidateResponse(emailLegacyTemplateListResponseSchema, response, {
        label: 'Get all legacy email templates',
        cause: 'ApiEmailTemplateRepository.getAllLegacyEmailTemplatesLegacy response schema validation failed',
        userMessage: 'Could not load legacy email templates',
      });
    } catch (error) {
      mapApiError(error, 'getAllLegacyEmailTemplatesLegacy', Endpoints.getAllLegacyEmailTemplates);
    }
  },
  upsert: async (emailTemplateRequest: {
    request: UpsertEmailTemplateRequest;
    existingVersionSegment?: string;
  }): Promise<EmailTemplate[]> => {
    const isUpdate =
      emailTemplateRequest.existingVersionSegment?.trim() &&
      emailTemplateRequest.existingVersionSegment !== EMAIL_TEMPLATE_VERSION_SEGMENT.VERSION;
    const url = isUpdate ? Endpoints.updateEmailTemplate(emailTemplateRequest.existingVersionSegment) : Endpoints.createEmailTemplate;

    try {
      const raw = isUpdate
        ? await notificationPreferencesClient.patch(url, emailTemplateRequest.request)
        : await notificationPreferencesClient.post(url, emailTemplateRequest.request);

      return parseAndValidateResponse(upsertEmailTemplatesResponseSchema, raw, {
        label: 'Email templates upsert',
        cause: 'ApiEmailTemplateRepository.upsert response schema validation failed',
        userMessage: 'Could not save email template',
        extraContext: { emailTemplateRequest },
      }).map((item) => ({
        ...item,
        scope: EMAIL_TEMPLATE_SCOPE.ACCOUNT,
      })) as EmailTemplate[];
    } catch (error) {
      mapApiError(error, 'upsert', url);
    }
  },
};
